/**
 * 认证状态管理 Store
 * 遵循 @240-state-management 规范
 *
 * 安全说明：Token 使用加密存储，防止明文暴露在 localStorage 中
 */
// TODO: [useApiQuery 迁移] 此 store 的 loading/try/catch/finally 模板代码可用 useApiQuery 声明式替换
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { LoginRequest, LoginResponse, MfaChallenge, PermissionCode, User } from '@/types'
import { ADMIN_ROLES, normalizeRole } from '@/types'
import { LoginResponseSchema } from '@/schemas/auth'
import { parseMfaChallenge } from '@/api/mfaChallenge'
import apiClient from '@/api'
import { getMyPermissions } from '@/api/me'
import { logger, tokenStorage, tokenHolder } from '@/utils'
import { normalizeUserDisplayName } from '@/utils/identity'
import type { RoutePermissionMeta } from '@/router/permissions'

interface RoutePermissionTarget {
  meta?: RoutePermissionMeta
}

type NormalizableUser = Omit<User, 'avatar'> & {
  avatar?: string | null
}

function looksLikeUserId(value: string, userId?: string): boolean {
  const normalized = value.trim()
  if (!normalized) {
    return false
  }
  if (userId && normalized === userId) {
    return true
  }
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)
}

function selectUserDisplayName(raw: NormalizableUser): string {
  const name = normalizeUserDisplayName(raw.name)
  const username = normalizeUserDisplayName(raw.username)
  if (name && !looksLikeUserId(name, raw.id)) {
    return name
  }
  if (username && !looksLikeUserId(username, raw.id)) {
    return username
  }
  return raw.email || 'Guest'
}

/**
 * 从 axios/fetch 风格错误中提取 HTTP 状态码。
 */
function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }
  const response = (error as { response?: unknown }).response
  if (typeof response !== 'object' || response === null) {
    return undefined
  }
  const status = (response as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

/**
 * 判断 profile 失败是否代表登录态明确失效。
 */
function shouldLogoutAfterProfileError(error: unknown): boolean {
  const status = getErrorStatus(error)
  return status === 401 || status === 403
}

/** 从请求异常里取 HTTP 状态码；没有响应（网络中断、超时）时返回 null */
function readHttpStatus(err: unknown): number | null {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status
  return typeof status === 'number' ? status : null
}

export const useAuthStore = defineStore('auth', () => {
  // ===== State =====
  const token = ref<string>('')
  const refreshToken = ref<string>('')
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  /** 最近一次登录失败的 HTTP 状态码；网络中断/超时为 null。登录页据此区分失败原因 */
  const lastLoginStatus = ref<number | null>(null)
  /** 口令正确但需要动态码时的票据与绑定数据；login 返回 false 且此值非空表示进入动态码步骤 */
  const pendingMfa = ref<MfaChallenge | null>(null)
  const userPermissions = ref<Set<PermissionCode>>(new Set())

  // ===== Getters =====
  const isAuthenticated = computed(() => !!token.value)

  const userName = computed(() => {
    return user.value ? selectUserDisplayName(user.value) : 'Guest'
  })

  /** 当前用户角色列表（兼容 roles 数组和单个 role 字段） */
  const userRoles = computed<string[]>(() => {
    if (user.value?.roles && user.value.roles.length > 0) {
      return user.value.roles.map(normalizeRole).filter(Boolean)
    }
    return user.value?.role ? [normalizeRole(user.value.role)] : ['member']
  })

  /** 当前用户是否为管理员 */
  const isAdmin = computed(() => {
    return userRoles.value.some(r => ADMIN_ROLES.has(r))
  })

  /**
   * 规范化 User 对象：确保 roles 字段始终为数组，
   * 并从 roles 数组同步推导 role 单值字段（兼容旧代码读取 user.role）
   */
  function normalizeUser(raw: NormalizableUser): User {
    const roles = raw.roles ?? []
    // 如果后端返回了 roles 数组但没有 role 字段，取第一个角色作为 role
    const role = raw.role ?? (roles.length > 0 ? roles[0] : undefined)
    const { avatar, ...rest } = raw
    return {
      ...rest,
      name: selectUserDisplayName(raw),
      ...(avatar ? { avatar } : {}),
      roles: roles.map(normalizeRole).filter(Boolean),
      ...(role !== undefined ? { role: normalizeRole(role) } : {}),
    }
  }

  // ===== Actions =====
  async function login(credentials: LoginRequest): Promise<boolean> {
    loading.value = true
    error.value = null
    lastLoginStatus.value = null
    pendingMfa.value = null

    try {
      const raw = await apiClient.post<LoginResponse>('/auth/login', credentials)
      const challenge = parseMfaChallenge(raw)
      if (challenge) {
        pendingMfa.value = challenge
        return false
      }
      // Zod 运行时校验：捕获后端返回结构变化
      const response = LoginResponseSchema.parse(raw)
      token.value = response.token
      refreshToken.value = response.refreshToken
      user.value = normalizeUser(response.user)

      // 同步更新内存中的 Token（供 API 请求使用）
      tokenHolder.setTokens(token.value, refreshToken.value)

      // 把真实 user.id（数据库 UUID）写入 localStorage，
      // 供 identity.ts 的 getUserId() 解析链使用（优先级4）
      try {
        if (response.user?.id) {
          localStorage.setItem('userId', response.user.id)
        }
        const loginUserName = selectUserDisplayName(response.user)
        if (loginUserName) {
          localStorage.setItem('userName', loginUserName)
        }
        if (response.user?.email) {
          localStorage.setItem('userEmail', response.user.email)
        }
      } catch { /* 忽略 localStorage 不可用场景 */ }

      // 使用加密存储保存 Token，防止明文暴露
      await tokenStorage.saveTokens(token.value, refreshToken.value)
      await loadPermissions()

      return true
    } catch (err) {
      error.value = (err as Error).message
      lastLoginStatus.value = readHttpStatus(err)
      logger.error('Login failed:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  function logout(): void {
    token.value = ''
    refreshToken.value = ''
    user.value = null
    userPermissions.value = new Set()
    // 清除内存中的 Token
    tokenHolder.clearTokens()
    // 清除加密存储的 Token
    tokenStorage.clearTokens()
    // 清除 identity.ts 缓存的 userId
    try {
      localStorage.removeItem('userId')
      localStorage.removeItem('userName')
      localStorage.removeItem('userEmail')
    } catch { /* ignore */ }
  }

  async function fetchProfile(): Promise<void> {
    if (!token.value) return

    loading.value = true
    try {
      const response = await apiClient.get<User>('/auth/profile')
      // 响应拦截器已返回 data.data，这里 response 就是 User
      user.value = normalizeUser(response)
      await loadPermissions()
      try {
        const profileUserName = selectUserDisplayName(response)
        if (profileUserName) {
          localStorage.setItem('userName', profileUserName)
        }
        if (response?.email) {
          localStorage.setItem('userEmail', response.email)
        }
      } catch { /* ignore */ }
    } catch (err) {
      if (shouldLogoutAfterProfileError(err)) {
        logger.error('Failed to fetch profile:', err)
        logout()
        return
      }
      logger.warn('Profile refresh failed, keeping existing auth state:', err)
    } finally {
      loading.value = false
    }
  }

  /** 并发防护：确保 initAuth 只执行一次 */
  let _initPromise: Promise<void> | null = null

  async function initAuth(): Promise<void> {
    if (_initPromise) return _initPromise
    _initPromise = _doInitAuth()
    return _initPromise
  }

  async function loadPermissions(): Promise<void> {
    try {
      const response = await getMyPermissions()
      userPermissions.value = new Set(response.permissions)
      if (user.value) {
        user.value = normalizeUser({
          ...user.value,
          roles: response.roles,
          permissions: response.permissions,
        })
      }
    } catch (err) {
      logger.warn('Failed to load permissions, falling back to role checks:', err)
      userPermissions.value = new Set()
    }
  }

  function can(code: PermissionCode): boolean {
    if (userPermissions.value.has('*') || userPermissions.value.has(code)) {
      return true
    }
    return isAdmin.value
  }

  function canAny(codes: readonly PermissionCode[]): boolean {
    return codes.some(code => can(code))
  }

  function canAccessRoute(routeOrItem: RoutePermissionTarget): boolean {
    const meta = routeOrItem.meta
    if (meta?.requiresAdmin && !isAdmin.value) {
      return false
    }
    const required = meta?.permissions ?? []
    return required.length === 0 || canAny(required)
  }

  function canInvokeAgent(agentId: string): boolean {
    if (!agentId) {
      return true
    }
    return can(`agent.${agentId}.invoke` as PermissionCode)
  }

  async function _doInitAuth(): Promise<void> {
    // E2E 测试旁路：仅当 window.__E2E_AUTH_BYPASS === true 时启用，
    // 用 Playwright addInitScript 注入；prod 不会触发。详见
    // apps/web-app/e2e/helpers/mock-auth.ts。
    if (typeof window !== 'undefined' && (window as Window & { __E2E_AUTH_BYPASS?: boolean }).__E2E_AUTH_BYPASS === true) {
      token.value = 'e2e-mock-token'
      refreshToken.value = 'e2e-mock-refresh-token'
      tokenHolder.setTokens(token.value, refreshToken.value)
      user.value = normalizeUser({
        id: 'e2e-user',
        username: 'e2e-tester',
        name: 'E2E Tester',
        email: 'e2e@datamanus.test',
        roles: ['admin', 'data_analyst'],
        permissions: ['*'],
      } as User)
      userPermissions.value = new Set<PermissionCode>(['*' as PermissionCode])
      return
    }
    try {
      // 从加密存储读取 Token
      const storedToken = await tokenStorage.getAccessToken()
      const storedRefreshToken = await tokenStorage.getRefreshToken()

      if (storedToken) {
        token.value = storedToken
        refreshToken.value = storedRefreshToken || ''
        // 同步更新内存中的 Token（供 API 请求使用）
        tokenHolder.setTokens(storedToken, storedRefreshToken || '')
        await fetchProfile()
      }
    } catch (err) {
      logger.error('初始化认证状态失败:', err)
      // 如果解密失败（可能是旧的未加密数据），清除并重新登录
      tokenHolder.clearTokens()
      tokenStorage.clearTokens()
    }
  }

  return {
    // State
    token,
    refreshToken,
    user,
    userPermissions,
    loading,
    error,
    lastLoginStatus,
    pendingMfa,
    // Getters
    isAuthenticated,
    userName,
    userRoles,
    isAdmin,
    can,
    canAny,
    canAccessRoute,
    canInvokeAgent,
    // Actions
    login,
    logout,
    fetchProfile,
    initAuth,
    loadPermissions
  }
})
